import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, User as _User, LogOut, ChevronRight, Shield, Compass, UserCircle, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../hooks/useTheme';
import MainSearch from '../search/MainSearch';
import Logo from '../common/Logo';
import NotificationBell from '../notifications/NotificationBell';
import { formatCentsToDollars } from '../../utils/formHelpers';

/**
 * Header Component
 * Responsive header with logo, navigation, search, and authentication controls
 */
export default function Header({ onAuthClick, showSearch = false, showReserveButton = false, reservePrice = 0, selectedTypes: _selectedTypes = [], onTypesChange: _onTypesChange = () => {}, showStickyCTA = false, onCtaClick = null }) {
  const { user, isAuthenticated, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [imageError, setImageError] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const navMenuRef = useRef(null);

  // Handle auth click - navigate to login if no custom handler provided
  const handleAuthClick = () => {
    if (onAuthClick) {
      onAuthClick();
    } else {
      navigate('/login');
    }
  };

  // Reset image error when user changes
  useEffect(() => {
    setImageError(false);
  }, [user?.profilePicture]);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (navMenuRef.current && !navMenuRef.current.contains(event.target)) {
        setShowNavMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
  };

  // Handle image loading errors
  const handleImageError = () => {
    console.log('[Header] Profile picture failed to load:', user?.profilePicture);
    setImageError(true);
  };

  // Helper to check if user has a specific role
  const hasRole = (role) => {
    return user?.roles?.includes(role);
  };

  // Get dashboard links based on user roles
  const getDashboardLinks = () => {
    const links = [];

    // Super Admin / Staff get Admin Dashboard
    if (hasRole('super_admin') || hasRole('staff')) {
      links.push({
        label: 'Admin Dashboard',
        path: '/admin/dashboard',
        icon: Shield,
      });
    }

    // Guides get Guide Dashboard
    if (hasRole('guide')) {
      links.push({
        label: 'Guide Dashboard',
        path: '/guide/dashboard',
        icon: Compass,
      });
    }

    // Everyone gets Customer Dashboard (or could be role-specific)
    links.push({
      label: 'Customer Dashboard',
      path: '/customer/dashboard',
      icon: UserCircle,
    });

    return links;
  };

  // Render user menu content (reusable for mobile and desktop)
  const renderUserMenuContent = () => (
    <>
      <div className="px-4 py-3 border-b border-gray-200">
        <p className="font-semibold">{user?.firstName} {user?.lastName}</p>
        <p className="text-sm text-gray-600">{user?.email}</p>
        <p className="text-xs text-gray-500 mt-1 capitalize">{user?.role}</p>
      </div>

      {/* Dashboard Links */}
      {getDashboardLinks().map((link) => {
        const IconComponent = link.icon;
        return (
          <Link
            key={link.path}
            to={link.path}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => setShowUserMenu(false)}
          >
            <IconComponent className="w-4 h-4" />
            <span>{link.label}</span>
          </Link>
        );
      })}

      <div className="border-t border-gray-200 my-2"></div>

      <Link
        to="/account/settings"
        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
        onClick={() => setShowUserMenu(false)}
      >
        <Settings className="w-4 h-4" />
        <span>Account Settings</span>
      </Link>
      <button
        onClick={handleLogout}
        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
      >
        <LogOut className="w-4 h-4" />
        <span>Logout</span>
      </button>
    </>
  );

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
      {/* Mobile Header */}
      <div className="md:hidden">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Combined Menu/Account Button */}
            {isAuthenticated ? (
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 border border-gray-300 rounded-full px-2 py-2 hover:shadow-md transition"
              >
                <Menu className="w-4 h-4" />
                {user?.profilePicture && !imageError ? (
                  <img
                    src={user.profilePicture}
                    alt={user.firstName || 'User'}
                    className="w-8 h-8 rounded-full object-cover"
                    onError={handleImageError}
                  />
                ) : (
                  <div className={`w-8 h-8 rounded-full ${theme.buttonPrimary.split(' ')[0]} flex items-center justify-center text-white text-sm font-semibold`}>
                    {user?.firstName?.[0] || user?.email?.[0] || 'U'}
                  </div>
                )}
              </button>
            ) : (
              <button
                onClick={handleAuthClick}
                className="flex items-center space-x-2 border border-gray-300 rounded-full px-2 py-2 hover:shadow-md transition"
              >
                <Menu className="w-4 h-4" />
                <_User className="w-5 h-5 text-gray-600" />
              </button>
            )}

            {/* Center: Logo */}
            <div className="flex-1 flex justify-center">
              <Logo />
            </div>

            {/* Right: Notification Bell (authenticated only) */}
            <div className="w-10">
              {isAuthenticated && <NotificationBell />}
            </div>
          </div>

          {showUserMenu && isAuthenticated && (
            <div className="absolute left-4 right-4 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              {renderUserMenuContent()}
            </div>
          )}
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-6 min-h-[64px]">
            <div className="flex items-center gap-4">
              {/* Navigation Menu Button */}
              <div className="relative" ref={navMenuRef}>
                <button
                  onClick={() => setShowNavMenu(!showNavMenu)}
                  className="p-2.5 hover:bg-gray-100 rounded-full transition"
                >
                  <Menu className="w-5 h-5" />
                </button>
                {showNavMenu && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <Link to="/" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition" onClick={() => setShowNavMenu(false)}>
                      Guides
                    </Link>
                    {!hasRole('guide') && (
                      <Link to="/guides/signup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition" onClick={() => setShowNavMenu(false)}>
                        Become a Guide
                      </Link>
                    )}
                  </div>
                )}
              </div>

              <Logo />
            </div>

            {showSearch && (
              <div className="flex-1 max-w-2xl">
                <MainSearch variant="inline" />
              </div>
            )}

            {/* Center: Sticky CTA when scrolled */}
            {showStickyCTA && !showSearch && !hasRole('guide') && (
              <div className="flex items-center gap-3 animate-slide-down">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Ready to join as a guide?</p>
                  <p className="text-xs text-gray-600">15-20 min • No credit card needed</p>
                </div>
                <button
                  onClick={onCtaClick}
                  className="inline-flex items-center px-6 py-2.5 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm ml-2"
                >
                  Create Your Profile
                  <ChevronRight className="ml-2 w-4 h-4" />
                </button>
              </div>
            )}

            <div className="flex items-center space-x-4">
              {showReserveButton && reservePrice > 0 && (
                <button className={`${theme.buttonPrimary} font-semibold px-6 py-2.5 rounded-lg transition shadow-md whitespace-nowrap`}>
                  Reserve - {formatCentsToDollars(reservePrice)}
                </button>
              )}

              {isAuthenticated ? (
                <>
                  {/* Notification Bell */}
                  <NotificationBell />

                  {/* User Menu */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center space-x-2 border border-gray-300 rounded-full px-1 py-1 hover:shadow-md transition"
                    >
                      {user?.profilePicture && !imageError ? (
                        <img
                          src={user.profilePicture}
                          alt={user.firstName || 'User'}
                          className="w-9 h-9 rounded-full object-cover"
                          onError={handleImageError}
                        />
                      ) : (
                        <div className={`w-9 h-9 rounded-full ${theme.buttonPrimary.split(' ')[0]} flex items-center justify-center text-white text-base font-semibold`}>
                          {user?.firstName?.[0] || user?.email?.[0] || 'U'}
                        </div>
                      )}
                    </button>
                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        {renderUserMenuContent()}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <button
                  onClick={handleAuthClick}
                  className="flex items-center space-x-2 border border-gray-300 rounded-full px-3 py-2 hover:shadow-md transition"
                >
                  <_User className="w-6 h-6 text-gray-600" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
